import { Controller } from "@hotwired/stimulus"
import { api, ApiError } from "../composables/useApi"

const SAVE_BAR_STATES = {
  idle:   { iconClass: "fas fa-pen",                  label: "Editing" },
  dirty:  { iconClass: "fas fa-circle",               label: "Unsaved changes" },
  saving: { iconClass: "fas fa-circle-notch fa-spin", label: "Saving…" },
  error:  { iconClass: "fas fa-triangle-exclamation", label: "Couldn't save" },
}

const HANDLE_FORMAT = /^[a-z][a-z0-9_]*$/i

// Tracked field names — every <input>/<select> with one of these `name`
// attributes contributes to the snapshot/diff.
const TRACKED_FIELDS = [
  "name", "handle",
  "country_code", "state_code", "city",
  "gender", "birth_year",
  "denomination", "political_affiliation",
  "education_level",
]

export default class extends Controller {
  static targets = [
    "viewOnly",
    "editOnly",
    "hero",
    "editButton",
    "nameInput",
    "handleInput",
    "handleField",
    "handleHelp",
    "promptChip",
    "saveBar",
    "saveStateIcon",
    "saveStateLabel",
  ]

  static values = {
    userId: Number,
    editing: { type: Boolean, default: false },
    saveState: { type: String, default: "idle" },
  }

  connect() {
    this.snapshot = this.captureSnapshot()
    this.applyEditingState()
    this.applySaveState()
    this.defaultHandleHelp = this.hasHandleHelpTarget ? this.handleHelpTarget.textContent.trim() : ""

    this.boundOnAvatarStaged = () => this.recomputeDirtyState()
    this.element.addEventListener("avatar:staged", this.boundOnAvatarStaged)
  }

  disconnect() {
    this.element.removeEventListener("avatar:staged", this.boundOnAvatarStaged)
  }

  // ---- Edit/View mode ---------------------------------------------------

  startEdit() {
    this.snapshot = this.captureSnapshot()
    this.editingValue = true
    this.saveStateValue = "idle"
  }

  startEditAndFocus(event) {
    const field = event.currentTarget?.dataset.field
    this.startEdit()
    if (field) requestAnimationFrame(() => this.focusField(field))
  }

  cancel() {
    this.clearHandleError()
    this.restoreSnapshot()
    this.revertAvatar()
    this.editingValue = false
    this.saveStateValue = "idle"
  }

  commit() {
    if (this.saveStateValue === "saving") return
    this.clearHandleError()

    // Block commit on invalid handle
    if (this.hasHandleInputTarget) {
      const handleError = this.validateHandle(this.handleInputTarget.value)
      if (handleError) {
        this.showHandleError(handleError)
        this.saveStateValue = "error"
        this.handleInputTarget.focus()
        return
      }
    }

    const diff = this.diffFields()
    const avatarController = this.getAvatarController()
    const stagedFile = avatarController?.getStagedFile() || null
    const stagedRemove = avatarController?.isMarkedForRemoval() || false

    // No-op commit: just exit edit mode.
    if (Object.keys(diff).length === 0 && !stagedFile && !stagedRemove) {
      this.editingValue = false
      this.saveStateValue = "idle"
      return
    }

    this.saveStateValue = "saving"

    const formData = new FormData()
    for (const [key, value] of Object.entries(diff)) {
      formData.append(`user[${key}]`, value)
    }
    if (stagedFile) formData.append("user[avatar]", stagedFile)
    if (stagedRemove) formData.append("user[remove_avatar]", "1")

    api.patch(`/users/${this.userIdValue}`, formData)
      .then(() => {
        // Server is the source of truth for display formatting (humanized
        // enums, country-name lookup, etc.) — reload to render the saved
        // state cleanly. Cancel never reaches here, so this only runs after
        // a successful commit.
        window.location.reload()
      })
      .catch((error) => {
        if (error instanceof ApiError) {
          this.handleCommitError(error.data?.errors || [])
        } else {
          this.handleCommitError(["Couldn't reach the server. Check your connection and try again."])
        }
      })
  }

  handleCommitError(errors) {
    this.saveStateValue = "error"
    const handleError = errors.find(msg => /handle/i.test(msg))
    if (handleError) this.showHandleError(handleError)

    if (this.hasSaveStateLabelTarget) {
      this.saveStateLabelTarget.textContent = errors[0] || SAVE_BAR_STATES.error.label
    }
  }

  editingValueChanged() {
    this.applyEditingState()
  }

  applyEditingState() {
    const editing = this.editingValue
    this.element.classList.toggle("is-editing", editing)
    this.viewOnlyTargets.forEach(el => { el.hidden = editing })
    this.editOnlyTargets.forEach(el => { el.hidden = !editing })
    if (this.hasEditButtonTarget) this.editButtonTarget.hidden = editing
  }

  focusField(field) {
    if (field === "name" && this.hasNameInputTarget) {
      this.nameInputTarget.focus()
      return
    }
    if (field === "handle" && this.hasHandleInputTarget) {
      this.handleInputTarget.focus()
      return
    }
    const row = this.element.querySelector(`.pf-demo-row[data-field="${CSS.escape(field)}"]`)
    if (!row) return
    const input = row.querySelector(".pf-demo-row__editor input, .pf-demo-row__editor select")
    if (input) input.focus()
  }

  // ---- Snapshot + diff --------------------------------------------------

  captureSnapshot() {
    const snap = {}
    TRACKED_FIELDS.forEach(name => { snap[name] = "" })
    this.element.querySelectorAll("[name]").forEach(el => {
      if (TRACKED_FIELDS.includes(el.name)) snap[el.name] = el.value ?? ""
    })
    return snap
  }

  currentValues() {
    const values = {}
    TRACKED_FIELDS.forEach(name => { values[name] = "" })
    this.element.querySelectorAll("[name]").forEach(el => {
      if (TRACKED_FIELDS.includes(el.name)) values[el.name] = el.value ?? ""
    })
    return values
  }

  diffFields() {
    const current = this.currentValues()
    const diff = {}
    for (const key of TRACKED_FIELDS) {
      if ((this.snapshot[key] ?? "") !== (current[key] ?? "")) {
        diff[key] = current[key]
      }
    }
    return diff
  }

  restoreSnapshot() {
    this.element.querySelectorAll("[name]").forEach(el => {
      if (TRACKED_FIELDS.includes(el.name) && el.name in this.snapshot) {
        el.value = this.snapshot[el.name]
      }
    })
    // Re-sync the country -> state cascade. The demographics controller
    // reloads state options based on the country select; we hand it the
    // snapshot's state_code so the right region is preselected after AJAX.
    const demoEl = this.element.querySelector("[data-controller~='profile-demographics']")
    if (demoEl) {
      const demoCtrl = this.application.getControllerForElementAndIdentifier(demoEl, "profile-demographics")
      if (demoCtrl) {
        demoCtrl.currentStateValue = this.snapshot.state_code || ""
        demoCtrl.countryChanged()
      }
    }
  }

  // ---- Field input handler --------------------------------------------

  fieldChanged(event) {
    const target = event?.target
    if (target === this.handleInputTarget) {
      this.applyHandleValidationFeedback()
    }
    this.recomputeDirtyState()
  }

  recomputeDirtyState() {
    // Don't override transient states the user needs to see.
    if (this.saveStateValue === "saving") return

    const fieldsDirty = Object.keys(this.diffFields()).length > 0
    const avatarController = this.getAvatarController()
    const avatarDirty = avatarController?.hasStagedChange() || false

    this.saveStateValue = (fieldsDirty || avatarDirty) ? "dirty" : "idle"
  }

  // ---- Avatar coordination ---------------------------------------------

  getAvatarController() {
    const el = this.element.querySelector("[data-controller~='avatar']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "avatar")
  }

  revertAvatar() {
    this.getAvatarController()?.revert()
  }

  // ---- Handle inline validation ----------------------------------------

  applyHandleValidationFeedback() {
    if (!this.hasHandleInputTarget) return
    const error = this.validateHandle(this.handleInputTarget.value)
    if (error) this.showHandleError(error)
    else this.clearHandleError()
  }

  validateHandle(handle) {
    if (!handle || handle.trim() === "") return null
    if (handle.length < 3) return "Handle must be 3+ characters"
    if (!HANDLE_FORMAT.test(handle)) return "Letters, numbers, underscores only — must start with a letter"
    return null
  }

  showHandleError(message) {
    if (this.hasHandleFieldTarget) this.handleFieldTarget.classList.add("has-error")
    if (this.hasHandleHelpTarget) {
      this.handleHelpTarget.textContent = message
      this.handleHelpTarget.classList.add("pf-help--error")
    }
  }

  clearHandleError() {
    if (this.hasHandleFieldTarget) this.handleFieldTarget.classList.remove("has-error")
    if (this.hasHandleHelpTarget) {
      this.handleHelpTarget.textContent = this.defaultHandleHelp
      this.handleHelpTarget.classList.remove("pf-help--error")
    }
  }

  // ---- Save bar pill ---------------------------------------------------

  saveStateValueChanged() {
    this.applySaveState()
  }

  applySaveState() {
    const state = this.saveStateValue
    const config = SAVE_BAR_STATES[state] || SAVE_BAR_STATES.idle
    if (this.hasSaveStateIconTarget)  this.saveStateIconTarget.className = config.iconClass
    if (this.hasSaveStateLabelTarget) this.saveStateLabelTarget.textContent = config.label
    if (this.hasSaveBarTarget)        this.saveBarTarget.dataset.state = state
  }
}
